import { Card, Checkbox, Typography } from 'antd';
import type { ChartOptions } from 'chart.js';
import 'chart.js/auto';
import moment from 'moment';
import React, {
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Line } from 'react-chartjs-2';
import {
    ChartPoint, SnapshotGroupBy,
    SnapshotItem, SnapshotItemGrouped,
    SnapshotStatsData,
    StatsItem,
} from '../../screens/InspectorCompass/interfaces';

const { Title, Text } = Typography;

export interface StatusChartsProps {
    snapshots: SnapshotItem[] | SnapshotItemGrouped[];
    loading?: boolean;
    groupBy?: SnapshotGroupBy;
    stats?: SnapshotStatsData | null;
    total?: number;
}

const commonChartLineOptions = {
    pointRadius: 5,
    fill: false,
    borderWidth: 2,
    tension: 0.3,
};

const StatusCharts: React.FC<StatusChartsProps> = ({
    snapshots, loading = false, groupBy, stats, total,
}) => {
    const [visibleDatasets, setVisibleDatasets] = useState({
        accepted: true,
        arrived: true,
        waiting: true,
        completed: true,
    });

    const [visibleItemsCount, setVisibleItemsCount] = useState(50);
    const chartPoints = useMemo<ChartPoint[]>(() => {
        const itemsToProcess = groupBy === SnapshotGroupBy.BOOKING
            ? (snapshots || []).slice(0, visibleItemsCount)
            : (snapshots || []);

        return itemsToProcess.map((item: SnapshotItem | SnapshotItemGrouped) => {
            if (groupBy === SnapshotGroupBy.BOOKING) {
                const currentItem = item as SnapshotItem;

                const created = moment(currentItem.createdAt);
                const acceptedMin = currentItem.accepted?.date ? moment(currentItem.accepted.date).diff(created, 'minutes') : 0;
                const arrivedMin = currentItem.arrivedAtPickup?.date ? moment(currentItem.arrivedAtPickup.date).diff(created, 'minutes') : 0;
                const completedMin = currentItem.completed?.date ? moment(currentItem.completed.date).diff(created, 'minutes') : 0;
                const waitingTimeMin = Math.floor(((currentItem.waitingTime?.time || 0) as number) / 60);
                return {
                    date: created.format('DD/MM/YYYY HH:mm'),
                    acceptedMin: acceptedMin < 0 ? 0 : acceptedMin,
                    arrivedMin: arrivedMin < 0 ? 0 : arrivedMin,
                    completedMin: completedMin < 0 ? 0 : completedMin,
                    waitingTimeMin: waitingTimeMin < 0 ? 0 : waitingTimeMin,
                    bookingNumericID: currentItem.bookingNumericID,
                    bookingID: currentItem.bookingID,
                };
            }
            const currentItem = item as SnapshotItemGrouped;

            return {
                date: currentItem.periodLabel,
                acceptedMin: Math.round(currentItem.averages.ACCEPTED.average / 60),
                arrivedMin: Math.round(currentItem.averages.ARRIVED_AT_PICKUP.average / 60),
                completedMin: Math.round(currentItem.averages.COMPLETED.average / 60),
                waitingTimeMin: Math.round(currentItem.averages.WAITING_TIME.average / 60),
                bookingNumericID: currentItem.bookingCount,
                bookingID: currentItem.periodLabel,
            };
        });
    }, [snapshots, groupBy, visibleItemsCount]);

    const summary = useMemo(() => {
        if (!stats) return null;

        const {
            ACCEPTED, ARRIVED_AT_PICKUP, COMPLETED, WAITING_TIME,
        } = stats;

        const convertToMinutes = (stat: StatsItem): StatsItem => ({
            average: Math.round(stat.average / 60),
            min: Math.round(stat.min / 60),
            max: Math.round(stat.max / 60),
        });

        return {
            total,
            stats: {
                ACCEPTED: convertToMinutes(ACCEPTED),
                ARRIVED_AT_PICKUP: convertToMinutes(ARRIVED_AT_PICKUP),
                COMPLETED: convertToMinutes(COMPLETED),
                WAITING_TIME: convertToMinutes(WAITING_TIME),
            },
        };
    }, [stats, total]);

    const tooltipHoverRef = useRef<boolean>(false);
    const tooltipTimeoutRef = useRef<number | null>(null);

    React.useEffect(() => () => {
        if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
        }
    }, []);

    const containerWidth = useMemo(
        () => Math.max(chartPoints.length * 80, 500),
        [chartPoints.length],
    );

    const labels = useMemo(
        () => chartPoints.map((p) => p.date),
        [chartPoints],
    );
    const bookingIds = useMemo(
        () => chartPoints.map((p) => p.bookingNumericID),
        [chartPoints],
    );
    const datasetAccepted = useMemo(
        () => chartPoints.map((p) => p.acceptedMin),
        [chartPoints],
    );
    const datasetArrived = useMemo(
        () => chartPoints.map((p) => p.arrivedMin),
        [chartPoints],
    );
    const datasetWaiting = useMemo(
        () => chartPoints.map((p) => p.waitingTimeMin),
        [chartPoints],
    );
    const datasetCompleted = useMemo(
        () => chartPoints.map((p) => p.completedMin),
        [chartPoints],
    );

    const data = useMemo(
        () => ({
            labels,
            datasets: [
                {
                    label: 'Accepted',
                    data: datasetAccepted,
                    borderColor: '#8884d8',
                    hidden: !visibleDatasets.accepted,
                    ...commonChartLineOptions,
                },
                {
                    label: 'Arrived at Pickup',
                    data: datasetArrived,
                    borderColor: '#82ca9d',
                    hidden: !visibleDatasets.arrived,
                    ...commonChartLineOptions,
                },
                {
                    label: 'Waiting Time',
                    data: datasetWaiting,
                    borderColor: '#ff6b6b',
                    hidden: !visibleDatasets.waiting,
                    ...commonChartLineOptions,
                },
                {
                    label: 'Completed',
                    data: datasetCompleted,
                    borderColor: '#ffc658',
                    hidden: !visibleDatasets.completed,
                    ...commonChartLineOptions,
                },
            ],
        }),
        [
            datasetAccepted,
            datasetArrived,
            datasetCompleted,
            datasetWaiting,
            labels,
            visibleDatasets,
        ],
    );

    const renderCustomTooltip = useCallback((index: number, chart: any): string => {
        const labelText = String(labels[index] || '');
        const numericText = String(bookingIds[index] || '');
        const bookingId = (chartPoints[index] as any)?.bookingID;
        const isGroupedView = groupBy !== SnapshotGroupBy.BOOKING;
        const href = !isGroupedView && bookingId ? `/v2/booking?id=${bookingId}` : '';

        const rowsHtml = (chart?.data?.datasets || [])
            .map((ds: any) => {
                const color = ds.borderColor;
                const name = ds.label;
                const value = ds.data?.[index];
                return [
                    '<div>',
                    `<span style="display:inline-block;width:10px;height:10px;background:${color};margin-right:6px;border-radius:50%"></span>`,
                    `${name}: ${value} min`,
                    '</div>',
                ].join('');
            })
            .join('');

        const headerHtmlParts = [
            `<div style="font-weight:600;margin-bottom:4px">${labelText}</div>`,
        ];

        if (isGroupedView) {
            headerHtmlParts.push(
                `<div style="margin-bottom:6px">Total de Bookings: ${numericText}</div>`,
            );
        } else if (href) {
            // eslint-disable-next-line max-len
            headerHtmlParts.push(`<div style="margin-bottom:6px">Booking: #${numericText} • <a href="${href}" target="_blank" rel="noopener noreferrer" style="color:#4096ff;">ver detalle</a></div>`);
        } else {
            headerHtmlParts.push(`<div style="margin-bottom:6px">Booking: #${numericText}</div>`);
        }

        const headerHtml = headerHtmlParts.join('');
        return [headerHtml, rowsHtml].join('');
    }, [labels, bookingIds, chartPoints, groupBy]);

    const hideTooltip = useCallback(() => {
        const el = document.getElementById('chartjs-inspector-tooltip');
        if (el) el.style.opacity = '0';
    }, []);
    const cancelHideTooltip = useCallback(() => {
        if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = null;
    }, []);
    const hideTooltipWithDelay = useCallback(() => {
        if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = window.setTimeout(() => {
            if (!tooltipHoverRef.current) {
                hideTooltip();
            }
        }, 200);
    }, [hideTooltip]);

    const handleDatasetToggle = useCallback((datasetKey: keyof typeof visibleDatasets) => {
        setVisibleDatasets((prev) => ({
            ...prev,
            [datasetKey]: !prev[datasetKey],
        }));
    }, []);

    const loadMoreItems = useCallback(() => {
        if (groupBy === SnapshotGroupBy.BOOKING && snapshots && visibleItemsCount < snapshots.length) {
            setVisibleItemsCount((prev) => Math.min(prev + 50, snapshots.length));
        }
    }, [groupBy, snapshots, visibleItemsCount]);

    const options: ChartOptions<'line'> = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index' as const, intersect: false },
            plugins: {
                legend: { display: false },
                title: { display: false },
                tooltip: {
                    enabled: false,
                    external: (context: any) => {
                        let tooltipEl = document.getElementById('chartjs-inspector-tooltip');
                        if (!tooltipEl) {
                            tooltipEl = document.createElement('div');
                            tooltipEl.id = 'chartjs-inspector-tooltip';
                            tooltipEl.style.position = 'absolute';
                            tooltipEl.style.pointerEvents = 'auto';
                            tooltipEl.style.background = '#1f1f1f';
                            tooltipEl.style.border = '1px solid #303030';
                            tooltipEl.style.borderRadius = '6px';
                            tooltipEl.style.color = '#fff';
                            tooltipEl.style.padding = '10px 12px';
                            tooltipEl.style.fontSize = '12px';
                            tooltipEl.style.zIndex = '1000';
                            tooltipEl.style.transition = 'opacity 0.15s ease';
                            document.body.appendChild(tooltipEl);
                            tooltipEl.addEventListener('mouseenter', () => {
                                tooltipHoverRef.current = true;
                                cancelHideTooltip();
                            });
                            tooltipEl.addEventListener('mouseleave', () => {
                                tooltipHoverRef.current = false;
                                hideTooltip();
                            });
                        }

                        const { chart, tooltip } = context;

                        if (!tooltip || tooltip.opacity === 0) {
                            // If the user is hovering over the tooltip itself, keep it visible
                            if (tooltipHoverRef.current) {
                                return;
                            }
                            hideTooltipWithDelay();
                            return;
                        }
                        cancelHideTooltip();

                        const dataIndex = tooltip.dataPoints?.[0]?.dataIndex || 0;
                        tooltipEl.innerHTML = renderCustomTooltip(dataIndex, chart);

                        const rect = chart.canvas.getBoundingClientRect();
                        tooltipEl.style.opacity = '1';
                        tooltipEl.style.left = `${rect.left + window.pageXOffset + tooltip.caretX + 12}px`;
                        tooltipEl.style.top = `${rect.top + window.pageYOffset + tooltip.caretY + 12}px`;
                    },
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    title: { display: true, text: 'Minutes' },
                },
                x: { ticks: { maxRotation: 45, minRotation: 0 } },
            },
            onHover: (event, elements) => {
                // Cancel hide tooltip when hovering over chart elements
                if (elements && elements.length > 0) {
                    cancelHideTooltip();
                }
            },
        }),
        [renderCustomTooltip, cancelHideTooltip, hideTooltipWithDelay],
    );

    return (
        <div>
            <Card bordered loading={loading} style={{ margin: '16px 0' }}>
                <div
                    style={{ overflowX: 'auto' }}
                    onScroll={(e) => {
                        const target = e.target as HTMLElement;
                        const scrollLeft = target.scrollLeft;
                        const scrollWidth = target.scrollWidth;
                        const clientWidth = target.clientWidth;

                        // Si el usuario llega al final del scroll horizontal y hay más elementos
                        if (scrollLeft + clientWidth >= scrollWidth - 10 && groupBy === SnapshotGroupBy.BOOKING) {
                            loadMoreItems();
                        }
                    }}
                >
                    <div
                        style={{
                            minWidth: '80%',
                            width: containerWidth,
                            height: 400,
                            margin: '0 auto',
                        }}
                    >
                        <Line options={options} data={data} />
                    </div>
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 24,
                        marginTop: 16,
                        flexWrap: 'wrap',
                    }}
                >
                    {[
                        { key: 'accepted', label: 'Accepted', color: '#8884d8' },
                        { key: 'arrived', label: 'Arrived at Pickup', color: '#82ca9d' },
                        { key: 'waiting', label: 'Waiting Time', color: '#ff6b6b' },
                        { key: 'completed', label: 'Completed', color: '#ffc658' },
                    ].map(({ key, label, color }) => (
                        <div
                            key={key}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                            }}
                        >
                            <Checkbox
                                checked={visibleDatasets[key as keyof typeof visibleDatasets]}
                                onChange={() => handleDatasetToggle(key as keyof typeof visibleDatasets)}
                                style={{ marginRight: 4 }}
                            />
                            <span
                                style={{
                                    display: 'inline-block',
                                    width: 12,
                                    height: 12,
                                    backgroundColor: color,
                                    borderRadius: '50%',
                                }}
                            />
                            <span style={{ fontSize: 14 }}>{label}</span>
                        </div>
                    ))}
                </div>
                {groupBy === SnapshotGroupBy.BOOKING && snapshots && snapshots.length > 50 && (
                    <div
                        style={{
                            textAlign: 'center',
                            marginTop: 8,
                            fontSize: 12,
                            color: '#666',
                        }}
                    >
                        Mostrando
                        {' '}
                        {visibleItemsCount}
                        {' '}
                        de
                        {' '}
                        {snapshots.length}
                        {' '}
                        bookings
                        {visibleItemsCount < snapshots.length && (
                            <span style={{ marginLeft: 8, color: '#1890ff' }}>
                                (Scroll hacia la derecha para cargar más)
                            </span>
                        )}
                    </div>
                )}
            </Card>
            {summary && (
                <Card bordered>
                    <Title level={4}>Resumen de Tiempos por Estado</Title>
                    <Text strong>
                        Total de Bookings Analizados:
                        {summary.total}
                    </Text>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns:
                                'repeat(auto-fit, minmax(220px, 1fr))',
                            gap: 16,
                            marginTop: 16,
                        }}
                    >
                        {([
                            { key: 'ACCEPTED', label: 'ACCEPTED' },
                            { key: 'ARRIVED_AT_PICKUP', label: 'ARRIVED AT PICKUP' },
                            { key: 'COMPLETED', label: 'COMPLETED' },
                            { key: 'WAITING_TIME', label: 'WAITING TIME' },
                        ] as const).map(({ key, label }) => {
                            const stat = (summary.stats as any)[key] as StatsItem;
                            return (
                                <Card key={key} size="small">
                                    <Text strong>{label}</Text>
                                    <ul style={{ paddingLeft: 16, marginTop: 8 }}>
                                        <li>
                                            Promedio:
                                            {stat.average}
                                            {' '}
                                            min
                                        </li>
                                        <li>
                                            Mínimo:
                                            {stat.min}
                                            {' '}
                                            min
                                        </li>
                                        <li>
                                            Máximo:
                                            {stat.max}
                                            {' '}
                                            min
                                        </li>
                                    </ul>
                                </Card>
                            );
                        })}
                    </div>
                </Card>
            )}
        </div>
    );
};

export default StatusCharts;
